﻿import { useState, useEffect } from 'react';
import Select from 'react-select';
import type { HaskQuestion } from '@/hooks/useHask';

interface Props {
  questions: HaskQuestion[];
  answers: Record<string, string>;
  onAnswer: (codattr: string, value: string) => void;
  originalAnswers?: Record<string, string>;
}

export default function QuestionForm({ questions, answers, onAnswer, originalAnswers }: Props) {
  const visibleQuestions = questions.filter(q => q.visibile !== 'N');
  if (visibleQuestions.length === 0) return null;

  return (
    <div className="row g-3 mt-2">
      {visibleQuestions.map(q => (
        <div key={q.codice} className={q.colspan && q.colspan.trim() !== '' ? q.colspan + ' mb-2' : 'col-12 mb-2'}>
          <QuestionField
            question={q}
            value={answers[q.codattr] || q.valore_predefinito || ''}
            onChange={val => onAnswer(q.codattr, val)}
            isModified={!!originalAnswers && originalAnswers[q.codattr] !== undefined && answers[q.codattr] !== originalAnswers[q.codattr]}
          />
        </div>
      ))}
    </div>
  );
}

const selectStyles = {
  control: (base: any) => ({ ...base, minHeight: 38, fontSize: 14, borderColor: '#ced4da' }),
  option: (base: any) => ({ ...base, fontSize: 13 }),
  multiValue: (base: any) => ({ ...base, fontSize: 12 }),
  placeholder: (base: any) => ({ ...base, fontSize: 13, color: '#94a3b8' }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

function autoResize(el: HTMLTextAreaElement) {
  el.style.height = 'auto';
  const maxHeight = 200;
  const newHeight = Math.max(el.scrollHeight, 32);
  el.style.height = Math.min(newHeight, maxHeight) + 'px';
  el.style.overflow = newHeight > maxHeight ? 'auto' : 'hidden';
}

function QuestionField({ question: q, value, onChange, isModified = false }: { question: HaskQuestion; value: string; onChange: (v: string) => void; isModified?: boolean }) {
  const [localValue, setLocalValue] = useState(value);
  useEffect(() => { setLocalValue(value); }, [value]);
  const isRequired = q.required === 'S';
  const modifiedStyle = isModified ? { borderLeft: '3px solid #ffc107', backgroundColor: '#fffdf0' } : {};
  const handleChange = (v: string) => { setLocalValue(v); };
  const handleBlur = () => { if (localValue !== value) onChange(localValue); };
  const disabled = q.readonly === 'S';
  const label = q.domanda + (isRequired ? ' *' : '');

  switch (q.tipo) {
    case 'SELECT':
    case 'RADIOIMAGE': {
      const options = buildOptions(q.risposte || []);
      const selected = options.find(o => 'value' in o && o.value === value) || null;
      return (
        <>
          <label className="form-label" style={{ fontSize: 11 }}>{label}</label>
          <Select
            options={options}
            value={selected}
            onChange={(opt: any) => onChange(opt?.value || '')}
            placeholder={q.domanda}
            isClearable
            isDisabled={disabled}
            isSearchable
            menuPortalTarget={document.body}
            styles={isModified ? { ...selectStyles, control: (base: any) => ({ ...selectStyles.control(base), borderLeft: '3px solid #ffc107', backgroundColor: '#fffdf0' }) } : selectStyles}
          />
        </>
      );
    }

    case 'MULTISELECT': {
      const options = (q.risposte || []).map(r => ({ value: r.codice, label: r.valore }));
      const selectedValues = value ? value.split(',') : [];
      const selected = options.filter(o => selectedValues.includes(o.value));
      return (
        <>
          <label className="form-label" style={{ fontSize: 11 }}>{label}</label>
          <Select
            options={options}
            value={selected}
            onChange={(opts: any) => onChange((opts || []).map((o: any) => o.value).join(','))}
            placeholder={q.domanda}
            isMulti
            isClearable
            isDisabled={disabled}
            isSearchable
            menuPortalTarget={document.body}
            styles={isModified ? { ...selectStyles, control: (base: any) => ({ ...selectStyles.control(base), borderLeft: '3px solid #ffc107', backgroundColor: '#fffdf0' }) } : selectStyles}
            closeMenuOnSelect={false}
          />
        </>
      );
    }

    case 'TEXTAREA':
      return (
        <>
          <label className="form-label" style={{ fontSize: 11 }}>{label}</label>
          <textarea
            className="form-control"
            style={{ fontSize: 11, overflow: 'hidden', resize: 'none', ...modifiedStyle }}
            rows={2}
            value={localValue}
            onChange={e => { handleChange(e.target.value); autoResize(e.target); }}
            onBlur={handleBlur}
            placeholder={q.domanda}
            disabled={disabled}
            required={isRequired}
            ref={el => { if (el) autoResize(el); }}
          />
        </>
      );

    case 'DATE':
      return (
        <>
          <label className="form-label" style={{ fontSize: 11 }}>{label}</label>
          <input
            type="date"
            className="form-control"
            style={modifiedStyle}
            value={localValue}
            onChange={e => { handleChange(e.target.value); onChange(e.target.value); }}
            disabled={disabled}
            required={isRequired}
          />
        </>
      );

    case 'CHECK':
      return (
        <div className="form-check form-switch pt-4">
          <input
            type="checkbox"
            className="form-check-input"
            id={'chk-' + q.codattr}
            checked={value === 'S'}
            onChange={e => onChange(e.target.checked ? 'S' : 'N')}
            disabled={disabled}
          />
          <label className="form-check-label fs-xs pt-2" htmlFor={'chk-' + q.codattr}>{label}</label>
        </div>
      );

    case 'NUM':
      return (
        <>
          <label className="form-label" style={{ fontSize: 11 }}>{label}</label>
          <input
            type="number"
            className="form-control"
            style={modifiedStyle}
            value={localValue}
            onChange={e => handleChange(e.target.value)}
            onBlur={handleBlur}
            placeholder={q.domanda}
            disabled={disabled}
            required={isRequired}
            min={q.validazione?.valmin}
            max={q.validazione?.valmax || undefined}
          />
        </>
      );

    case 'TEXT':
    default:
      return (
        <>
          <label className="form-label" style={{ fontSize: 11 }}>{label}</label>
          <input
            type="text"
            className="form-control"
            style={modifiedStyle}
            value={localValue}
            onChange={e => handleChange(e.target.value)}
            onBlur={handleBlur}
            placeholder={q.domanda}
            disabled={disabled}
            required={isRequired}
          />
        </>
      );
  }
}

function buildOptions(risposte: HaskQuestion['risposte']): any[] {
  if (!risposte || risposte.length === 0) return [];

  const hasGroups = risposte.some(r => r.etichetta && r.etichetta.trim() !== '');
  if (!hasGroups) {
    return risposte.map(r => ({ value: r.codice, label: r.valore }));
  }

  // Grouped options for react-select
  const groups: { label: string; options: { value: string; label: string }[] }[] = [];
  let cur = '';
  risposte.forEach(r => {
    const lbl = r.etichetta?.trim() || '';
    if (lbl !== cur) { cur = lbl; groups.push({ label: lbl, options: [] }); }
    groups[groups.length - 1].options.push({ value: r.codice, label: r.valore });
  });
  return groups;
}










